import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, Icon } from '@resy/ui-kitchen';
import ReservationButton from '../../../../../../../ui-kitchen/src/components/ReservationButton';
import Bell from '../../../../../../../ui-kitchen/src/svg/icons/bell.svg';
import ALLOWED_DOMAINS from '../../../../../../../ui-kitchen/src/constants/allowedDomains';
import { handleMktAnalytics } from '../../../AmexMarketingUtils/AmexUtils';
import { CTA_BOOKING } from '../../../AmexMarketingUtils/constants';
import './AmexPlatinumReservationButtonListRefresh.scss';
import { DINNER_SERVICE_TYPE_ID } from '../../constants';

const AmexPlatinumReservationButtonListRefresh = (
  {
    venueId,
    selectedDate,
    selectedSeats,
    activeNotifications,
    loadNotifyModal,
    venueName,
    Analytics,
    featureRecaptcha,
    limit,
    limitSlots,
    reservations,
    availableServiceTypes,
    ariaDescribedBy,
  }
) => {
  const [newNotifications, setNewNotifications] = useState(false);
  const dinnerSlots = reservations?.filter((slot) => slot.serviceTypeId === DINNER_SERVICE_TYPE_ID);

  const handleNotification = (notifications) => {
    if (Object.values(notifications).every((notification) => notification === null)) {
      if (newNotifications) {
        setNewNotifications(false);
        return;
      }
      return;
    }
    setNewNotifications(true);
  };

  const onPostMessage = (e) => {
    if (!ALLOWED_DOMAINS.includes(e.origin)) {
      return;
    }

    if (e.data.event === 'onNotifySync' && e.data.venueId === venueId) {
      handleNotification(e.data.value);
    }
  };

  useEffect(() => {
    window.addEventListener('message', onPostMessage);
    return () => window.removeEventListener('message', onPostMessage);
  }, []);

  const onReservationSelect = (reservation, notifyService) => {
    // This will append a parameter to the widget url
    // File : packages/widget/app/embed.js
    const reservationConfig = {
      venueId,
      src: 'resy.com-venue-card',
      date: moment(selectedDate).format('YYYY-MM-DD'),
      seats: selectedSeats,
      ...reservation?.token && { tableConfigId: reservation.token },
    };

    if (notifyService) {
      loadNotifyModal({
        activeNotifications,
        selectedDate,
        selectedQuantity: selectedSeats,
        notifyService,
        venueId,
        venueName,
      });
      return;
    }

    resyWidget.openModal(reservationConfig, reservation);
  };

  const onSelect = ({
    e, reservation, notifyService, analyticsLabel,
  }) => {
    e.stopPropagation();

    if (Analytics.event) {
      const extraPrams = {
        action_name: `${analyticsLabel} - Click`,
        item_name: venueName,
      };
      handleMktAnalytics(Analytics?.event, CTA_BOOKING, extraPrams);
    }
    onReservationSelect(reservation, notifyService);
  };

  const renderReservation = (reservation) => {
    const variant = (reservation.isGda) ? 'rga' : undefined;
    let ariaLabel = null;

    if (reservation.isGda) {
      ariaLabel = `GDA Time Slot, ${moment(reservation.time, 'YYYY-MM-DD HH:mm:ss').format('LT')} ${reservation.type}`;
    }

    return (
      <ReservationButton
        id={reservation.token}
        key={reservation.token}
        onClick={(e) => onSelect({ e, reservation: { featureRecaptcha, ...reservation }, analyticsLabel: 'Dine-in brick' })}
        badge={reservation.badge}
        variant={variant}
        colors={reservation.isGda ? null : reservation.colors}
        time={reservation.time}
        tableType={reservation.type}
        aria-label={ariaLabel}
        ariaDescribedBy={ariaDescribedBy}
      />
    );
  };

  const renderReservations = () => {
    const slots = limitSlots ? dinnerSlots?.slice(0, limit) : dinnerSlots;
    return slots?.map(renderReservation);
  };

  const renderViewMore = () => (
    <Button
      aria-describedby={ariaDescribedBy}
      data-test-id="AmexPlatinumReservationButtonListRefresh-button-view-more"
      aria-label={`View more availability for ${venueName}`}
      type="button"
      className="AmexPlatinumReservationButtonListRefresh__view-more"
      onClick={(e) => onSelect({ e, analyticsLabel: 'Ellipsis' })}
      variant="secondary"
    >
      <span className="separator" />
      <span className="separator" />
      <span className="separator" />
    </Button>
  );

  const getActiveNotifySet = () => {
    const
      activeNotifySet = Object.values(activeNotifications).some((notifies) => notifies !== null);

    return activeNotifySet || newNotifications;
  };

  const renderNotify = () => {
    const activeNotifySet = getActiveNotifySet();

    let fallback = 'Notify';

    if (activeNotifySet) {
      fallback = 'Notify On';
    }

    let notifyShiftId;

    if (availableServiceTypes[0]) {
      notifyShiftId = parseInt(availableServiceTypes[0], 10);
    }

    return (
      <Button
        aria-describedby={ariaDescribedBy}
        aria-label={fallback}
        type="button"
        data-test-id="amexplatinumnights-notify-button"
        className={cx('AmexPlatinumReservationButtonListRefresh__notify', {
          'AmexPlatinumReservationButtonListRefresh__notify--active': activeNotifySet,
        })}
        onClick={(e) => onSelect({
          e, stId: null, analyticsLabel: 'Notify', notifyService: notifyShiftId,
        })}
        variant={activeNotifySet ? 'primary' : 'secondary'}
      >
        <Icon iconClass="bell" component={Bell} />
        <span className="notify-msg">{fallback}</span>
      </Button>
    );
  };

  return (
    <div
      className="AmexPlatinumReservationButtonListRefresh"
    >
      {renderReservations()}
      {(dinnerSlots.length > limit) ? renderViewMore() : renderNotify()}
    </div>
  );
};

export default AmexPlatinumReservationButtonListRefresh;

AmexPlatinumReservationButtonListRefresh.propTypes = {
  selectedDate: PropTypes.string.isRequired,
  selectedSeats: PropTypes.number.isRequired,
  loadNotifyModal: PropTypes.func.isRequired,
  activeNotifications: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  featureRecaptcha: PropTypes.bool.isRequired,
  limit: PropTypes.number.isRequired,
  limitSlots: PropTypes.bool.isRequired,
  reservations: PropTypes.arrayOf(
    PropTypes.object
  ).isRequired,
  venueId: PropTypes.number.isRequired,
  venueName: PropTypes.string.isRequired,
  availableServiceTypes: PropTypes.arrayOf(
    PropTypes.string
  ).isRequired,
  ariaDescribedBy: PropTypes.string.isRequired,
};
